/**
 * Gmail orders parser. Read-only Gmail scan for e-commerce confirmations
 * from India's top 7 e-tailers: Amazon, Flipkart, Myntra, Ajio, Nykaa,
 * Tata-Cliq, Meesho.
 *
 * v0: mock mode default. Live mode requires GmailOAuth.refreshAccessToken
 * and a Cloudflare-Pages-Function PKCE proxy (deferred). The parser logic
 * is fully implemented and unit-tested against the mock fixtures.
 */
import type { WardrobeOrderItem, Provenance } from './types.ts';
import { paise } from './types.ts';
import { MOCK_WARDROBE_ORDERS } from './mock-data.ts';
import { ensureFreshToken, gmailFetch, type OAuthToken, GmailOAuth } from './gmail-oauth.ts';

const SENDER_QUERIES: ReadonlyArray<{ q: string; merchant: WardrobeOrderItem['merchant'] }> = [
  { q: 'from:(auto-confirm@amazon.in OR shipment-tracking@amazon.in) subject:"Your Amazon.in order"', merchant: 'amazon' },
  { q: 'from:(noreply@flipkart.com OR orders@flipkart.com) subject:(order confirmed OR has been placed)', merchant: 'flipkart' },
  { q: 'from:(orders@myntra.com OR noreply@myntra.com) subject:(order confirmed OR successfully placed)', merchant: 'myntra' },
  { q: 'from:(noreply@ajio.com OR alerts@ajio.com) subject:(your order)', merchant: 'ajio' },
  { q: 'from:(orders@nykaa.com OR noreply@nykaa.com) subject:(thank you for your order)', merchant: 'nykaa' },
  { q: 'from:(noreply@tatacliq.com OR cs.support@tatacliq.com) subject:(order confirmation)', merchant: 'tata-cliq' },
  { q: 'from:(transactional@meesho.com OR helpdesk@meesho.com) subject:(order confirmed)', merchant: 'meesho' },
];

interface GmailListResp {
  messages?: ReadonlyArray<{ id: string; threadId: string }>;
  nextPageToken?: string;
  resultSizeEstimate: number;
}
interface GmailMsgResp {
  id: string;
  internalDate: string;
  snippet: string;
  payload: {
    headers: ReadonlyArray<{ name: string; value: string }>;
    parts?: ReadonlyArray<GmailPart>;
    body?: { data?: string };
    mimeType?: string;
  };
}
interface GmailPart {
  mimeType?: string;
  filename?: string;
  body?: { data?: string; attachmentId?: string };
  parts?: ReadonlyArray<GmailPart>;
}

export interface GmailOrdersOpts {
  readonly mode: 'mock' | 'live';
  readonly oauth?: GmailOAuth;
  readonly token?: OAuthToken;
  readonly since_days?: number;
  readonly max_messages?: number;
}

export async function fetchOrders(opts: GmailOrdersOpts): Promise<readonly WardrobeOrderItem[]> {
  if (opts.mode === 'mock') return MOCK_WARDROBE_ORDERS;
  if (!opts.oauth || !opts.token) throw new Error('live mode requires oauth + token');

  const token = await ensureFreshToken(opts.oauth, opts.token);
  const since = opts.since_days ?? 730;
  const max_per = opts.max_messages ?? 80;

  const all: WardrobeOrderItem[] = [];
  for (const def of SENDER_QUERIES) {
    const q = `${def.q} after:${Math.floor((Date.now() - since * 86_400_000) / 1000)}`;
    let pageToken: string | undefined;
    let collected = 0;
    while (collected < max_per) {
      const listUrl = new URL('https://gmail.googleapis.com/gmail/v1/users/me/messages');
      listUrl.searchParams.set('q', q);
      listUrl.searchParams.set('maxResults', String(Math.min(50, max_per - collected)));
      if (pageToken) listUrl.searchParams.set('pageToken', pageToken);
      const list = await gmailFetch<GmailListResp>(token, listUrl.toString());
      if (!list.messages || list.messages.length === 0) break;

      for (const m of list.messages) {
        const msg = await gmailFetch<GmailMsgResp>(
          token,
          `https://gmail.googleapis.com/gmail/v1/users/me/messages/${m.id}?format=full`,
        );
        const parsed = parseMerchantMessage(def.merchant, msg);
        if (parsed) all.push(parsed);
        collected += 1;
      }
      pageToken = list.nextPageToken;
      if (!pageToken) break;
    }
  }
  return all;
}

export function parseMerchantMessage(
  merchant: WardrobeOrderItem['merchant'],
  msg: GmailMsgResp,
): WardrobeOrderItem | null {
  const subject = headerValue(msg, 'Subject') ?? '';
  const body = decodePayloadBody(msg.payload);
  if (!body) return null;

  const order_id =
    extractFirst(body, /(?:Order\s*(?:#|ID|Number)[:\s]*)([A-Z0-9-]{6,20})/i) ??
    extractFirst(subject, /\b([A-Z]{2,4}-?\d{7,12})\b/) ??
    `${merchant}-${msg.id.slice(0, 8)}`;

  const totalMatch =
    extractFirst(body, /(?:Total|Order\s*Total|Grand\s*Total|Amount\s*Paid|To\s*Pay)[:\s]*(?:Rs\.?|INR|₹)\s*([0-9,]+(?:\.[0-9]{1,2})?)/i) ??
    extractFirst(body, /(?:Rs\.?|INR|₹)\s*([0-9,]+(?:\.[0-9]{1,2})?)\s*(?:\(includes\b|total)/i);
  const total_rupees = totalMatch ? Number(totalMatch.replace(/,/g, '')) : 0;

  const item_titles = extractAllTitles(body);
  if (item_titles.length === 0 && total_rupees === 0) return null;

  const provenance: Provenance = {
    connector: 'gmail-orders',
    source_id: msg.id,
    fetched_at: Date.now(),
  };
  return {
    merchant,
    order_id,
    order_date: new Date(Number(msg.internalDate)).toISOString().slice(0, 10),
    total: paise(total_rupees * 100),
    items: item_titles.length
      ? item_titles.map((t) => {
          const cat = classifyCategory(t.title);
          return {
            title: t.title,
            ...(t.brand ? { brand: t.brand } : {}),
            ...(cat ? { category: cat } : {}),
            qty: t.qty,
            unit_price: paise(t.price_rupees * 100),
            ...(t.photo_url ? { photo_url: t.photo_url } : {}),
          };
        })
      : [
          {
            title: subject.replace(/^.*?:\s*/, '').slice(0, 80),
            qty: 1,
            unit_price: paise(total_rupees * 100),
          },
        ],
    provenance,
  };
}

function headerValue(msg: GmailMsgResp, name: string): string | undefined {
  return msg.payload.headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value;
}
function decodePayloadBody(p: GmailMsgResp['payload']): string | null {
  const collect = (part: { mimeType?: string; body?: { data?: string }; parts?: ReadonlyArray<GmailPart> }): string => {
    const data = part.body?.data;
    if (data && (part.mimeType ?? '').startsWith('text/')) {
      try { return atob(data.replace(/-/g, '+').replace(/_/g, '/')); } catch { return ''; }
    }
    let out = '';
    for (const sub of part.parts ?? []) out += collect(sub);
    return out;
  };
  const text = collect(p);
  return text ? stripHtml(text) : null;
}
function stripHtml(s: string): string {
  return s
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}
function extractFirst(s: string, re: RegExp): string | null {
  const m = s.match(re);
  return m && m[1] ? m[1] : null;
}
function extractAllTitles(body: string): ReadonlyArray<{ title: string; brand?: string; qty: number; price_rupees: number; photo_url?: string }> {
  const out: Array<{ title: string; brand?: string; qty: number; price_rupees: number; photo_url?: string }> = [];
  const lineRe = /([A-Z][A-Za-z0-9\s&'.,/-]{4,60})\s+(?:Qty[:\s]*|x\s*)?(\d{1,2})\s+(?:Rs\.?|INR|₹)\s*([0-9,]+(?:\.[0-9]{1,2})?)/g;
  let m: RegExpExecArray | null;
  while ((m = lineRe.exec(body)) !== null) {
    const title = (m[1] ?? '').trim();
    const qty = Number(m[2] ?? '1');
    const price = Number((m[3] ?? '0').replace(/,/g, ''));
    if (title.length > 0 && price > 0) {
      const brand = title.split(/\s+/)[0];
      out.push({ title, ...(brand ? { brand } : {}), qty, price_rupees: price });
    }
    if (out.length >= 12) break;
  }
  return out;
}
const CATEGORY_KEYWORDS: ReadonlyArray<{ re: RegExp; cat: string }> = [
  { re: /\b(jeans|trouser|pant|chino|joggers|leggings|shorts)\b/i, cat: 'bottoms' },
  { re: /\b(shirt|polo|tee|t-shirt|kurta|kurti|top|blouse|hoodie|sweater|sweatshirt)\b/i, cat: 'tops' },
  { re: /\b(saree|lehenga|salwar|sherwani|bandhgala|dupatta|anarkali)\b/i, cat: 'ethnic' },
  { re: /\b(jacket|coat|blazer|overcoat|windcheater|parka)\b/i, cat: 'outerwear' },
  { re: /\b(sneaker|shoe|loafer|boot|flat|heel|sandal|chappal|kolhapuri|mojari)\b/i, cat: 'shoes' },
  { re: /\b(robe|nightwear|pajama|loungewear|innerwear|brief|boxer)\b/i, cat: 'loungewear' },
  { re: /\b(belt|wallet|watch|sunglass|tie|scarf|stole|cap|hat|bag|backpack)\b/i, cat: 'accessory' },
];
function classifyCategory(title: string): string | undefined {
  for (const { re, cat } of CATEGORY_KEYWORDS) if (re.test(title)) return cat;
  return undefined;
}
